import { Injectable } from '@angular/core';
import {
    BYTES_PER_MB,
    THOUSAND,
    THUMBNAIL_PIXEL_DIMENSIONS,
    base64ToDataUrl
} from '@caballus/common';
import { Camera, CameraResultType, CameraSource, ImageOptions, Photo } from '@capacitor/camera';
import { CameraPermissionType } from '@capacitor/camera/dist/esm/definitions';
import { Capacitor } from '@capacitor/core';
import { ConnectionStatus, Network } from '@capacitor/network';
import { FilePicker } from '@capawesome/capacitor-file-picker';
import { FileSelectorPlugin } from '@ion-caballus/core/plugins';
import { ThumbnailService } from '@ion-caballus/core/services/thumbnail.service';
import { Platform } from '@ionic/angular';
import { ToastrService } from 'ngx-toastr';
import { Observable, Observer, defer, from, of } from 'rxjs';
import { catchError, filter, map, share, switchMap, tap } from 'rxjs/operators';
import { MediaSelectionSource, ModalService, Video } from '../../../';

const DEFAULT_PHOTO_QUANTITY = 60;
export const ERR_NOT_AUTHORIZED = 'NOT_AUTHORIZED';
export const ERR_NO_LOCATION_PERMISSION = 'Location permissions required for Map my Ride';
const CAMERA_PERMISSIONS_GRANTED = 'granted';

export const DEFAULT_IMAGE_OPTIONS: ImageOptions = {
    quality: 50,
    width: 2160,
    height: 2160,
    allowEditing: true,
    resultType: CameraResultType.DataUrl,
    saveToGallery: false
};

/*
    Service allows access to plugin features
    though dependency injection in place of
    direct imports, which helps with testing
    of other classes because the provider can
    be overridden with a mock implementation
*/
@Injectable({ providedIn: 'root' })
export class CapacitorPluginServiceShim {
    public networkStatus$: Observable<ConnectionStatus>;

    constructor(private readonly _toastService: ToastrService) {
        this.networkStatus$ = defer(() => this._createNetworkStatusStream()).pipe(share());
    }

    public networkStatus(): Observable<ConnectionStatus> {
        return from(Network.getStatus());
    }

    public async getPhoto(options: ImageOptions): Promise<Photo> {
        const response = await Camera.getPhoto(options);
        return response;
    }

    public async checkPermissions(source?: CameraSource): Promise<boolean> {
        const permissions = await Camera.checkPermissions();
        if (source === CameraSource.Camera) {
            return permissions.camera === CAMERA_PERMISSIONS_GRANTED;
        } else if (source === CameraSource.Photos) {
            return permissions.photos === CAMERA_PERMISSIONS_GRANTED;
        }
        return (
            permissions.camera === CAMERA_PERMISSIONS_GRANTED &&
            permissions.photos === CAMERA_PERMISSIONS_GRANTED
        );
    }

    public async requestCameraPermission(permissionType: CameraPermissionType): Promise<boolean> {
        const permissions = await Camera.requestPermissions({
            permissions: [permissionType]
        });

        if (permissionType === 'camera') {
            return permissions.camera === CAMERA_PERMISSIONS_GRANTED;
        }
        return permissions.photos === CAMERA_PERMISSIONS_GRANTED;
    }

    private _createNetworkStatusStream(): Observable<ConnectionStatus> {
        return new Observable(subscriber => {
            const handle = Network.addListener('networkStatusChange', event => {
                this._toastService.info('Network Status' + event);
                subscriber.next(event);
            });

            // Get initial status
            Network.getStatus().then(status => subscriber.next(status));

            return () => {
                //handle.remove();
            };
        });
    }
}

@Injectable({ providedIn: 'root' })
export class CapacitorPluginService {
    constructor(
        private readonly _platform: Platform,
        private readonly _shim: CapacitorPluginServiceShim,
        private readonly _toastService: ToastrService,
        private readonly _thumbnailService: ThumbnailService,
        private readonly _modalService: ModalService
    ) {}

    public async getAttachment(fileInput?: HTMLInputElement, quality?: number): Promise<Blob> {
        // const { Camera } = Plugins;
        if (this._platform.is('cordova')) {
            const image = await Camera.getPhoto({
                quality: quality || DEFAULT_PHOTO_QUANTITY,
                resultType: CameraResultType.Base64,
                allowEditing: false
            });

            // Read into Blob
            const bytes = [];
            const buffer = atob(image.base64String);
            for (let i = 0; i < buffer.length; i++) {
                bytes.push(buffer.charCodeAt(i));
            }
            return new Blob([new Uint8Array(bytes)], {
                // Capacitor camera currently only outputs jpeg
                type: 'image/jpeg'
            });
        } else {
            if (fileInput) {
                return new Promise<Blob>((resolve, reject): void => {
                    const orgOnChange = fileInput.onchange;
                    fileInput.onchange = (_): void => {
                        fileInput.onchange = orgOnChange;
                        if (fileInput.files.length > 0) {
                            const file = fileInput.files.item(0);
                            resolve(file);
                        } else {
                            reject();
                        }
                    };
                    fileInput.click();
                });
            } else {
                return null;
            }
        }
    }

    public networkStatus(): Observable<ConnectionStatus> {
        return this._shim.networkStatus();
    }

    public getPhoto(options: ImageOptions = DEFAULT_IMAGE_OPTIONS): Observable<Photo> {
        return from(this._shim.getPhoto(options));
    }

    public checkPermissions(source?: CameraSource): Observable<boolean> {
        return from(this._shim.checkPermissions(source));
    }

    public requestCameraPermission(permissionType: CameraPermissionType): Observable<boolean> {
        return from(this._shim.requestCameraPermission(permissionType));
    }

    public isNativeAppPlatform(): {
        platform: string;
        isNative: boolean;
    } {
        return {
            platform: Capacitor.getPlatform(),
            isNative: Capacitor.isNativePlatform()
        };
    }

    public getVideoAttachment(): Observable<{
        originalFilePath: string;
        thumbnail: string;
        fileSize: number;
        fileName: string;
    }> {
        if (Capacitor.isNativePlatform()) {
            return from(
                FilePicker.pickVideos({
                    
                })
            ).pipe(
                map(res => res.files[0]),
                switchMap(file =>
                    from(
                        FileSelectorPlugin.GeneRateThumbnail({
                            filePath: file.path
                        })
                    ).pipe(
                        map(res => ({
                            originalFilePath: file.path,
                            thumbnail: base64ToDataUrl(res.thumbnail),
                            fileSize: res.fileSize,
                            fileName: file.path.split('/').pop()
                        }))
                    )
                )
            );
        } else {
            const fileInput: HTMLInputElement = document.createElement('INPUT') as HTMLInputElement;
            fileInput.hidden = true;
            fileInput.setAttribute('type', 'file');
            fileInput.setAttribute('id', 'filePicker');
            fileInput.setAttribute('accept', 'video/*');
            fileInput.setAttribute('multiple', 'false');

            return new Observable(
                (
                    observer: Observer<{
                        originalFilePath: string;
                        thumbnail: string;
                        fileSize: number;
                        fileName: string;
                    }>
                ): void => {
                    const orgOnChange = fileInput.onchange;
                    fileInput.onchange = (_): void => {
                        fileInput.onchange = orgOnChange;
                        if (fileInput.files.length > 0) {
                            const item = fileInput.files.item(0);
                            if (item.size >= BYTES_PER_MB * THOUSAND) {
                                this._toastService.error(
                                    'The maximum size allowed for an uploaded file is 1 GB.'
                                );
                                observer.error(null);
                            }

                            // generate thumbnail
                            this._thumbnailService
                                .getVideoThumbnail(
                                    {
                                        blob: (): Promise<Blob> =>
                                            Promise.resolve(fileInput.files.item(0))
                                    } as Video,
                                    THUMBNAIL_PIXEL_DIMENSIONS,
                                    THUMBNAIL_PIXEL_DIMENSIONS
                                )
                                .subscribe({
                                    next: urlString => {
                                        const {
                                            thumbnailDataUrl,
                                            dataUrl
                                        }: {
                                            thumbnailDataUrl: string;
                                            dataUrl: string;
                                        } = JSON.parse(urlString);
                                        // emit the result
                                        observer.next({
                                            thumbnail: thumbnailDataUrl,
                                            originalFilePath: dataUrl,
                                            fileSize: item.size,
                                            fileName: item.name
                                        });
                                        // complete the observable
                                        observer.complete();
                                    },
                                    error: error => {
                                        observer.error(error);
                                    }
                                });
                        } else {
                            observer.error({});
                        }
                    };
                    fileInput.onerror = (e): void => {
                        observer.error('File upload failed');
                    };
                    fileInput.click();
                }
            );
        }
    }

    public getDocumentAttachment(): Observable<{
        originalFilePath: string;
        fileSize: number;
        fileName: string;
    }> {
        if (Capacitor.isNativePlatform()) {
            return from(
                FilePicker.pickFiles({
                    
                    types: [
                        'application/pdf',
                        'application/zip',
                        'application/msword',
                        'application/vnd.ms-excel',
                        'text/plain'
                    ]
                })
            ).pipe(
                map(res => res.files[0]),
                map(file => ({
                    originalFilePath: file.path,
                    fileSize: file.size,
                    fileName: file.name
                })),
                catchError(err => {
                    return of(err);
                })
            );
        } else {
            const fileInput: HTMLInputElement = document.createElement('INPUT') as HTMLInputElement;
            fileInput.hidden = true;
            fileInput.setAttribute('type', 'file');
            fileInput.setAttribute('id', 'filePicker');
            fileInput.setAttribute('accept', '.zip,.pdf,.docx,.xlsx,.txt');
            fileInput.setAttribute('multiple', 'false');

            return new Observable(
                (
                    observer: Observer<{
                        originalFilePath: string;
                        fileSize: number;
                        fileName: string;
                    }>
                ): void => {
                    const orgOnChange = fileInput.onchange;
                    fileInput.onchange = (): void => {
                        fileInput.onchange = orgOnChange;
                        if (fileInput.files.length > 0) {
                            const item = fileInput.files.item(0);
                            if (item.size >= BYTES_PER_MB * THOUSAND) {
                                this._toastService.error(
                                    'The maximum size allowed for an uploaded file is 1 GB.'
                                );
                                observer.error(null);
                            }

                            const dataUrl = URL.createObjectURL(item);

                            observer.next({
                                originalFilePath: dataUrl,
                                fileSize: item.size,
                                fileName: item.name
                            });
                            // complete the observable
                            observer.complete();
                        } else {
                            observer.error({});
                        }
                    };
                    fileInput.onerror = (e): void => {
                        observer.error('File upload failed');
                    };
                    fileInput.click();
                }
            );
        }
    }

    public selectMediaToUpload(): Observable<MediaSelectionSource> {
        const isWeb = !Capacitor.isNativePlatform();
        return this._modalService
            .media(isWeb)
            .afterClosed()
            .pipe(
                map(response => ({
                    cancelled:
                        response === null || response === undefined || response.toString() === '',
                    source: response
                })),
                filter(modalResponse => !modalResponse.cancelled),
                switchMap(modalResponse => {
                    // PICK_PHOTO & TAKE_PHOTO & check for permissions
                    if (
                        [MediaSelectionSource.PICK_PHOTO, MediaSelectionSource.TAKE_PHOTO].includes(
                            modalResponse.source
                        )
                    ) {
                        const source: CameraSource =
                            modalResponse.source === MediaSelectionSource.PICK_PHOTO
                                ? CameraSource.Photos
                                : CameraSource.Camera;
                        return this._handlePermissions(source, modalResponse);
                    }
                    // PICK_VIDEO & TAKE_VIDEO & check for permissions
                    if (
                        [MediaSelectionSource.PICK_VIDEO, MediaSelectionSource.TAKE_VIDEO].includes(
                            modalResponse.source
                        )
                    ) {
                        const source: CameraSource =
                            modalResponse.source === MediaSelectionSource.PICK_VIDEO
                                ? CameraSource.Photos
                                : CameraSource.Camera;
                        return this._handlePermissions(source, modalResponse);
                    }

                    if (modalResponse.source === MediaSelectionSource.PICK_DOCUMENT) {
                        return of(modalResponse);
                    }

                    return of({
                        ...modalResponse,
                        cancelled: true
                    });
                }),
                filter(modalResponse => !modalResponse.cancelled),
                map(modalResponse => modalResponse.source)
            );
    }

    private _handlePermissions(
        source: CameraSource.Camera | CameraSource.Photos,
        modalResponse: { cancelled: boolean; source: MediaSelectionSource }
    ) {
        return this.checkPermissions(source).pipe(
            switchMap(permission => {
                if (!permission) {
                    return this.requestCameraPermission(
                        source === CameraSource.Camera ? 'camera' : 'photos'
                    ).pipe(
                        map(result => {
                            if (!result) {
                                this._toastService.info(
                                    'Please go to your phone settings to allow Caballus access to your photos or videos'
                                );
                                modalResponse.cancelled = true;
                            }

                            return modalResponse;
                        })
                    );
                }
                return of(modalResponse);
            })
        );
    }
}
